import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import classnames from 'classnames';

import Controls from './Controls';
import { setSelectedFaceInd } from '../actions';
import { resizeObserver } from '../../../../helpers/browser';
import photoPlaceholder from '../../../../assets/images/photo-placeholder.svg';
import videoPlaceholder from '../../../../assets/images/video-placeholder.svg';
import env from '../../../../config/env';

import style from './style.module.scss';

const setImgWrapperSize = (domEl) => {
  if (!domEl) {
    return;
  }

  const size = Math.min(domEl.clientHeight, domEl.clientWidth);
  domEl.parentNode?.style.setProperty('--imgSize', `${size}px`);
};

const Image = ({
  hidden,
  smallURI,
  largeImageUrl,
  sharedURI,
  imageMiningData = [],
  displayFaceBoxes,
  selectedFaceInd,
  setSelectedFaceIndAction,
}) => {
  const imgRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setImgWrapperSize(imgRef.current));
  }, [imgRef.current]);

  useEffect(() => resizeObserver(imgRef.current, setImgWrapperSize), [imgRef.current]);

  const isAiApp = env.USER_APP_TYPE === 'ai';
  const displayUrl = isAiApp ? (sharedURI || largeImageUrl) : largeImageUrl;
  const url = hidden ? smallURI : displayUrl;
  const isVideo = url?.endsWith('.mp4') || false;

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [url]);

  if (!url || hasError) {
    return (
      <div className={style.imgWrapper}>
        <div className={style.errorPlaceholder}>
          <img
            src={isVideo ? videoPlaceholder : photoPlaceholder}
            alt="Media unavailable"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={style.imgWrapper}>
      {isLoading && <Spinner animation="border" variant="primary" className={style.spinner} />}

      {/* TODO: refactor LightBox to support multiple media types for images and videos */}
      {isVideo ? (
        <video
          ref={imgRef}
          id="videoSnapshot"
          key={url}
          onLoadedData={() => setIsLoading(false)}
          onError={() => setHasError(true)}
          autoPlay
          muted
          loop
          playsInline
          alt="snapshotVideo"
        >
          <source src={url} />
          Your browser does not support HTML video.
        </video>
      ) : (
        <img
          id="lightboxImage"
          ref={imgRef}
          src={url}
          alt="Snapshot"
          onLoad={() => setIsLoading(false)}
          onError={() => setHasError(true)}
        />
      )}
      <div className={style.facesData}>
        {displayFaceBoxes && !isLoading && !hasError
          && imageMiningData.map(({ BoundingBox: boundingBox }, ind) => {
            const top = `${boundingBox.Top * 100}%`;
            const left = `${boundingBox.Left * 100}%`;
            const height = `${boundingBox.Height * 100}%`;
            const width = `${boundingBox.Width * 100}%`;

            return (
              // eslint-disable-next-line
              <div
                key={JSON.stringify(boundingBox)}
                className={classnames(
                  style.faceBoundingBox,
                  { [style.active]: ind === selectedFaceInd },
                )}
                style={{
                  top,
                  left,
                  width,
                  height,
                  cursor: 'pointer',
                  zIndex: 3,
                }}
                onClick={() => setSelectedFaceIndAction(ind)}
              />
            );
          })}
      </div>
      <Controls />
    </div>
  );
};

export default connect(
  ({
    lightbox: {
      snapshot: {
        data: { hidden, smallURI, sharedURI } = {},
        largeImageUrl,
        imageMiningData: {
          data: imageMiningData,
          selectedInd: selectedFaceInd,
        },
      },
      info: {
        imageMiningData: {
          open: displayFaceBoxes,
        },
      },
    },
  }) => ({
    hidden,
    smallURI,
    largeImageUrl,
    sharedURI,
    imageMiningData,
    displayFaceBoxes,
    selectedFaceInd,
  }),
  { setSelectedFaceIndAction: setSelectedFaceInd },
)(Image);
